import { useState } from 'react'
import useMediaQuery from 'hooks/useMediaQuery'
import { FlavorQuantitySelector } from 'new-components/FlavorQuantitySelector'
import PromotionalPrice from 'new-components/MarketPlace/PromotionalPrice'
import { ProgressiveQuantityDropdown, ProgressiveTierShape } from 'new-components/ProgressiveQuantityDropdown'
import * as Style from '@cannect/pages/NewCart/Product/styles'
import { MOBILE_WIDTH } from 'utils/constants'
import { TRelatedProduct } from '@cannect/services/resources/progressive-discounts'
import { ProgressiveGroupOffer } from './ProgressiveGroupOffer'

export type ProgressiveGroupVariant = {
  id: string | number
  qty: number
  name: string
  flavor: string
  /** main_product_id da família de sabores — usado pelo FlavorQuantitySelector pra buscar as variações. */
  mainProductId?: number
  imageUrl?: string
  tiers: ProgressiveTierShape[]
  unitFmt: string
  fullUnitFmt?: string
  /** Preço unitário final (após desconto progressivo), em número. */
  unitFinal: number
  /** Preço base (sem desconto progressivo), em número. */
  baseUnit: number
  hasDiscount: boolean
  discountPct?: number
  totalFmt: string
  /** true → faixas são de benefício de FRETE (item não muda de preço); repassado ao dropdown de quantidade. */
  isShipping?: boolean
  isOpen: boolean
  isLoading?: boolean
  onToggle: () => void
  onRemove: () => void
  onSelect: (n: number) => void | Promise<void>
}

export type ProgressiveGroupMilestone = { label: string; reached: boolean }

export type ProgressiveGroupCardProps = {
  groupTitle: string
  subtitle?: string
  groupOff: string
  groupQty: number
  nextHint: string
  progressPct: number
  milestones: ProgressiveGroupMilestone[]
  variants: ProgressiveGroupVariant[]
  hasSavings: boolean
  savingsFmt?: string
  groupTotalFmt: string
  /** Layout da linha de variação no card amplo: 'full' (RowFull, ex.: /carrinho-wa) ou 'sheet' (RowSheet, ex.: /carrinho). */
  variantRow?: 'full' | 'sheet'
  /** Produtos da mesma regra ainda fora do carrinho (oferta cruzada). */
  offerProducts?: TRelatedProduct[]
  onAdd?: (productId: number) => void | Promise<void>
  /** Percentual de desconto ativo do grupo — repassado ao ProgressiveGroupOffer. */
  groupDiscountPct?: number
}

export type ProgressiveGroupHeaderProps = {
  groupTitle: string
  subtitle?: string
  groupOff: string
  groupQty: number
  nextHint: string
  progressPct: number
  milestones: ProgressiveGroupMilestone[]
  /** false = oculta badge/nome/subtitle/'Faixa atual' (mantém barra e milestones). Default: true. */
  showIdentification?: boolean
}

const GREEN = '#748351'

const RowSpinner = () => (
  <svg className="animate-spin" width={20} height={20} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke={GREEN} strokeWidth="2.5" opacity={0.25} />
    <path d="M12 3a9 9 0 0 1 9 9" stroke={GREEN} strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

interface ProgressiveGroupVariantRowProps {
  v: ProgressiveGroupVariant
  groupQty: number
  /** Renderiza o dropdown via portal (necessário no sheet p/ escapar de overflow). Padrão false. */
  overlay?: boolean
}

/**
 * Linha de variação para o CARRINHO AMPLO — reutiliza os styled-components de cardProduct.tsx
 * (Container, ProductInfos, ProductName, ProductProperties, Quantity, ProductSabor, ProductPrice, ProductTotal)
 * e o PromotionalPrice, garantindo espaçamento idêntico ao /carrinho-wa.
 */
export function ProgressiveGroupVariantRowFull({ v, groupQty }: ProgressiveGroupVariantRowProps) {
  const isMobile = useMediaQuery(MOBILE_WIDTH)

  return (
    <div style={{ position: 'relative', opacity: v.isLoading ? 0.6 : 1, pointerEvents: v.isLoading ? 'none' : 'auto' }}>
      <Style.Container isMobile={isMobile}>
        <Style.ProductInfos isMobile={isMobile}>
          {v.imageUrl ? (
            <img src={v.imageUrl} alt="foto produto" />
          ) : (
            <div
              style={{
                width: 67,
                height: 70,
                flex: 'none',
                borderRadius: 6,
                background: '#edece7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 7,
                fontWeight: 700,
                letterSpacing: '.1em',
                color: GREEN
              }}>
              CANNECT
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Style.ProductName isMobile={isMobile}>{v.name}</Style.ProductName>
          </div>
        </Style.ProductInfos>

        <Style.ProductProperties isMobile={isMobile}>
          <Style.Quantity>
            <ProgressiveQuantityDropdown
              qty={v.qty}
              tiers={v.tiers}
              mode="group"
              groupQty={groupQty}
              open={v.isOpen}
              overlay={false}
              isShipping={v.isShipping}
              onToggle={v.onToggle}
              onSelect={v.onSelect}
              onRemove={v.onRemove}
            />
          </Style.Quantity>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <FlavorQuantitySelector
              productId={Number(v.id)}
              mainProductId={v.mainProductId}
              currentFlavorLabel={v.flavor}
            />
          </div>

          <Style.ProductPrice isMobile={isMobile}>
            <PromotionalPrice
              new_price={v.unitFinal}
              old_price={v.hasDiscount ? v.baseUnit : undefined}
              discount={v.hasDiscount && v.discountPct ? String(v.discountPct) : undefined}
              discount_type="percent"
            />
          </Style.ProductPrice>

          <Style.ProductTotal isMobile={isMobile}>{v.totalFmt}</Style.ProductTotal>
        </Style.ProductProperties>
      </Style.Container>
      {v.isLoading && (
        <div
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RowSpinner />
        </div>
      )}
    </div>
  )
}

/**
 * Linha de variação para o SHEET LATERAL — espelha o layout de CartItem (CartItems.tsx):
 * flex items-start com imagem 80×80, coluna com nome, sabor, dropdown (portal) e preço.
 */
export function ProgressiveGroupVariantRowSheet({ v, groupQty, overlay = false }: ProgressiveGroupVariantRowProps) {
  return (
    <div
      className={[
        'relative flex items-start gap-3 rounded-lg border border-gray-100 bg-white p-3',
        v.isLoading ? 'pointer-events-none opacity-60' : ''
      ]
        .filter(Boolean)
        .join(' ')}>
      {v.imageUrl ? (
        <img src={v.imageUrl} alt={v.name} className="h-[80px] w-[80px] flex-shrink-0 rounded-lg object-contain" />
      ) : (
        <div
          style={{
            width: 80,
            height: 80,
            flex: 'none',
            borderRadius: 8,
            background: '#edece7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: '.1em',
            color: GREEN
          }}>
          CANNECT
        </div>
      )}

      <div className="flex flex-1 flex-col gap-2">
        <p className="text-xs font-medium text-gray-800 sm:text-sm">{v.name}</p>
        <FlavorQuantitySelector
          productId={Number(v.id)}
          mainProductId={v.mainProductId}
          currentFlavorLabel={v.flavor}
          overlay={overlay}
          className="text-xs text-gray-600 sm:text-sm"
        />
        <div className="mt-2 flex flex-row items-center justify-between gap-2">
          <ProgressiveQuantityDropdown
            qty={v.qty}
            tiers={v.tiers}
            mode="group"
            groupQty={groupQty}
            open={v.isOpen}
            overlay={overlay}
            isShipping={v.isShipping}
            onToggle={v.onToggle}
            onSelect={v.onSelect}
            onRemove={v.onRemove}
          />
          <div className="flex flex-col items-end">
            {v.hasDiscount && !!v.fullUnitFmt && (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-gray-400 line-through">{v.fullUnitFmt}</span>
                <span className="font-medium text-green-600">-{Math.trunc(v.discountPct || 0)}%</span>
              </div>
            )}
            <span className="text-sm font-semibold text-gray-900">{v.unitFmt}</span>
          </div>
        </div>
      </div>

      {v.isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
          <RowSpinner />
        </div>
      )}
    </div>
  )
}

const TagIcon = ({ size = 14, color = '#fff' }: { size?: number; color?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M7.5 3h6l7.5 7.5a2 2 0 0 1 0 2.8l-4.7 4.7a2 2 0 0 1-2.8 0L6 10.5V4.5A1.5 1.5 0 0 1 7.5 3z" />
  </svg>
)

/** Header reutilizável: badge + título + barra de progresso + milestones.
 *  showIdentification=false oculta o bloco badge/nome/subtitle/'Faixa atual' — mantém apenas progresso. */
export function ProgressiveGroupHeader({
  groupTitle,
  subtitle,
  groupOff,
  groupQty,
  nextHint,
  progressPct,
  milestones,
  showIdentification = true
}: ProgressiveGroupHeaderProps) {
  return (
    <>
      {/* Linha de identificação: badge + nome + faixa atual (ocultável no sheet compacto) */}
      {showIdentification && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
            padding: '15px 20px',
            borderBottom: '1px solid #e9edd5'
          }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: GREEN,
              color: '#fff',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '.03em',
              padding: '6px 12px',
              borderRadius: 999
            }}>
            <TagIcon />
            DESCONTO EM GRUPO
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#3f3f3f' }}>{groupTitle}</span>
          {!!subtitle && <span style={{ fontSize: 13, color: '#8a8a8a' }}>{subtitle}</span>}
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#8a8a8a' }}>Faixa atual do grupo</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: GREEN }}>{groupOff}</div>
          </div>
        </div>
      )}

      {/* Barra de progresso */}
      <div style={{ padding: '14px 20px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 12.5, color: '#7a7a7a' }}>
            Total do grupo: <strong style={{ color: '#3f3f3f' }}>{groupQty} un</strong>
          </span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: GREEN }}>{nextHint}</span>
        </div>
        <div style={{ position: 'relative', height: 10, background: '#eaeede', borderRadius: 999, overflow: 'hidden' }}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${Math.max(0, Math.min(100, progressPct))}%`,
              background: 'linear-gradient(90deg,#8c9e61,#748351)',
              borderRadius: 999,
              transition: 'width .25s ease'
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 7 }}>
          {milestones.map((ms) => (
            <span key={ms.label} style={{ fontSize: 11, fontWeight: 600, color: ms.reached ? GREEN : '#b3b3ab' }}>
              {ms.label}
            </span>
          ))}
        </div>
      </div>
    </>
  )
}

export function ProgressiveGroupCard({
  groupTitle,
  subtitle,
  groupOff,
  groupQty,
  nextHint,
  progressPct,
  milestones,
  variants,
  hasSavings,
  savingsFmt,
  variantRow = 'full',
  offerProducts,
  onAdd,
  groupDiscountPct
}: ProgressiveGroupCardProps) {
  // Incrementado ao alterar quantidade de qualquer variante — sinaliza a oferta para abrir
  const [offerSignal, setOfferSignal] = useState(0)

  const wrappedVariants = variants.map((v) => ({
    ...v,
    onSelect: (n: number) => {
      v.onSelect(n)
      if (offerProducts?.length) setOfferSignal((s) => s + 1)
    }
  }))

  return (
    <div
      style={{
        border: '1.5px solid #dfe4c9',
        borderRadius: 14,
        margin: 14,
        overflow: 'visible',
        background: '#fcfdf7'
      }}>
      <ProgressiveGroupHeader
        groupTitle={groupTitle}
        subtitle={subtitle}
        groupOff={groupOff}
        groupQty={groupQty}
        nextHint={nextHint}
        progressPct={progressPct}
        milestones={milestones}
        showIdentification={false}
      />

      {/* Variantes — RowFull (padrão, ex.: /carrinho-wa) ou RowSheet (ex.: /carrinho) */}
      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {wrappedVariants.map((v) =>
          variantRow === 'sheet' ? (
            <ProgressiveGroupVariantRowSheet key={v.id} v={v} groupQty={groupQty} />
          ) : (
            <ProgressiveGroupVariantRowFull key={v.id} v={v} groupQty={groupQty} />
          )
        )}
      </div>

      {/* Oferta cruzada: outros produtos da mesma regra (colapsável) */}
      {!!offerProducts?.length && !!onAdd && (
        <ProgressiveGroupOffer
          products={offerProducts}
          mode={variantRow === 'sheet' ? 'sheet' : 'full'}
          onAdd={onAdd}
          openSignal={offerSignal}
          groupDiscountPct={groupDiscountPct}
        />
      )}

      {/* Footer */}
      {hasSavings && !!savingsFmt && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '12px 20px',
            borderTop: '1px solid #e9edd5',
            background: '#f4f6e9',
            borderRadius: '0 0 12px 12px'
          }}>
          <span style={{ fontSize: 13, color: '#8a8a8a' }}>
            economia: <strong style={{ color: GREEN }}>{savingsFmt}</strong>
          </span>
        </div>
      )}
    </div>
  )
}
